import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { BookOpen, Mail, Lock, User, Eye, EyeOff, Star, Shield, Check } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import SEO from '../components/SEO';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';

const Login = () => {
  const navigate = useNavigate();
  const { login, register } = useAuth();
  const { t } = useLanguage();
  const [isRegister, setIsRegister] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({ email: '', password: '', name: '' });
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (isRegister) {
        await register(formData.email, formData.password, formData.name);
        toast.success(t('login.success_register'));
      } else {
        await login(formData.email, formData.password);
        toast.success(t('login.success_login'));
      }
      navigate('/');
    } catch (error) {
      const message = error.response?.data?.detail || error.message || t('misc.error');
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12 md:pt-24" data-testid="login-page">
      <SEO title="Inloggen — Oma's Verhaaltjes" description="Log in op Oma's Verhaaltjes om alle slaapverhaaltjes te lezen, voortgang op te slaan en de voorleesstem te activeren." noindex />
      <div className="w-full max-w-4xl grid md:grid-cols-2 gap-8 items-center">
        {/* Left: Benefits */}
        <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} className="hidden md:block">
          <h2 className="font-display text-4xl text-foreground mb-6">
            {isRegister ? t('login.join') : t('login.welcome')}
          </h2>
          <p className="text-muted-foreground text-lg mb-8 leading-relaxed">
            {isRegister ? t('login.join_sub') : t('login.welcome_sub')}
          </p>
          <div className="space-y-4">
            {[t('login.benefit1'), t('login.benefit2'), t('login.benefit3'), t('login.benefit4')].map((benefit, i) => (
              <motion.div key={i} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.2 + i * 0.1 }} className="flex items-center gap-3">
                <div className="w-6 h-6 rounded-full bg-gold/20 flex items-center justify-center flex-shrink-0">
                  <Check className="w-3.5 h-3.5 text-gold" />
                </div>
                <span className="text-foreground">{benefit}</span>
              </motion.div>
            ))}
          </div>
          <div className="mt-8 flex items-center gap-3 text-sm text-muted-foreground">
            <div className="flex">
              {[1, 2, 3, 4, 5].map((s) => <Star key={s} className="w-4 h-4 text-gold fill-gold" />)}
            </div>
            <span>{t('login.loved_by')}</span>
          </div>
        </motion.div>

        {/* Right: Login Form */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full">
          <Card className="border-2 border-border shadow-xl">
            <CardHeader className="text-center">
              <div className="mx-auto w-14 h-14 rounded-2xl bg-gold/20 flex items-center justify-center mb-3">
                <BookOpen className="w-7 h-7 text-gold" />
              </div>
              <CardTitle className="font-display text-3xl">
                {isRegister ? t('login.register_title') : t('login.title')}
              </CardTitle>
              <CardDescription className="font-accent">
                {isRegister ? t('login.register_sub') : t('login.welcome_back_sub')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                {isRegister && (
                  <div className="space-y-2">
                    <Label htmlFor="name" className="font-accent">{t('login.name')}</Label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input id="name" data-testid="register-name" type="text" placeholder={t('login.name_placeholder')} value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required={isRegister} className="pl-10" />
                    </div>
                  </div>
                )}
                <div className="space-y-2">
                  <Label htmlFor="email" className="font-accent">{t('login.email')}</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input id="email" data-testid="login-email" type="email" placeholder="oma@verhaaltjes.nl" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} required className="pl-10" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password" className="font-accent">{t('login.password')}</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input id="password" data-testid="login-password" type={showPassword ? "text" : "password"} placeholder="••••••••" value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })} required className="pl-10 pr-10" />
                    <button type="button" data-testid="toggle-password-visibility" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors">
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  {!isRegister && (
                    <div className="text-right">
                      <Link to="/reset-password" data-testid="forgot-password-link" className="text-sm text-gold hover:text-gold/80 font-accent transition-colors">
                        {t('login.forgot')}
                      </Link>
                    </div>
                  )}
                </div>
                <Button type="submit" data-testid="login-submit" disabled={loading} className="w-full rounded-full bg-old-rose hover:bg-old-rose/80 text-night-blue font-bold py-5">
                  {loading ? (
                    <div className="w-5 h-5 border-2 border-night-blue/30 border-t-night-blue rounded-full animate-spin" />
                  ) : isRegister ? t('login.register_submit') : t('login.submit')}
                </Button>
              </form>
              <div className="mt-5 text-center">
                <button type="button" data-testid="toggle-auth-mode" onClick={() => setIsRegister(!isRegister)} className="text-sm text-muted-foreground hover:text-foreground font-accent transition-colors">
                  {isRegister ? t('login.toggle_login') : t('login.toggle_register')}
                </button>
              </div>
              <div className="mt-4 flex items-center justify-center gap-2 text-xs text-muted-foreground">
                <Shield className="w-3.5 h-3.5" />
                <span>{t('login.safe')}</span>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </div>
  );
};

export default Login;
